import { useContext, useState } from "react";
import TablePage from "../../components/table-page/TablePage";
import {
  getAdStatusOptionValues,
  getSipNumbersData,
  getSipNumberStatusOptionValues,
  getTypeOptionValues,
  ResponseGuiType,
  TransformedDataType,
} from "../../api/numbers/getSipNumbersData";
import AddNumberAssignmentFormModal from "./add-number-assignment-form-modal/AddNumberAssignmentFormModal";
import { Badge, Button, Tooltip } from "antd";
import EditNumberAssignmentFormModal from "./edit-number-assignment-form-modal/EditNumberAssignmentFormModal";
import EditNumberStatusFormModal from "./edit-number-status-form-modal/EditNumberStatusFormModal";
import Edit from "@ant-design/icons/EditOutlined";
import WarningFilled from "@ant-design/icons/WarningFilled";
import TextHighlighter from "../../utils/text-highlighter/TextHighlighter";
import { exportToExcel } from "../../utils/exportToExcel";
import EditPoolFormModal from "../pool/edit-pool-form-modal/EditPoolFormModal";
import { GlobalContext } from "../../components/layout/global-context/globalContext";
import { AiOutlineFileExcel } from "react-icons/ai";
import { MdAddCircleOutline, MdChatBubbleOutline } from "react-icons/md";

interface State {
  isAddNumberModalVisible: boolean;
  isEditNumberAssignmentModalVisible: boolean;
  isEditNumberStatusModalVisible: boolean;
  isEditPoolModalVisible: boolean;
  editNumberRecord?: TransformedDataType;
  refreshDataToggle: boolean;
  dataView: TransformedDataType[];
  sipNumberStatusFilterOptionValues: string[];
  adStatusFilterOptionValues: string[];
  typeFilterOptionValues: string[];
}

function Numbers() {
  const { user } = useContext(GlobalContext);
  const [state, setState] = useState<State>({
    isAddNumberModalVisible: false,
    isEditNumberAssignmentModalVisible: false,
    isEditNumberStatusModalVisible: false,
    isEditPoolModalVisible: false,
    editNumberRecord: undefined as TransformedDataType | undefined,
    refreshDataToggle: true,
    dataView: [] as TransformedDataType[],
    sipNumberStatusFilterOptionValues: [],
    adStatusFilterOptionValues: [],
    typeFilterOptionValues: [],
  });

  const {
    isAddNumberModalVisible,
    isEditNumberAssignmentModalVisible,
    isEditNumberStatusModalVisible,
    isEditPoolModalVisible,
    editNumberRecord,
    refreshDataToggle,
    dataView,
    sipNumberStatusFilterOptionValues,
    adStatusFilterOptionValues,
    typeFilterOptionValues,
  } = state;

  const buttons = [
    {
      title: (
        <span className="flex">
          <AiOutlineFileExcel className="text-lg mr-1" />
          Export
        </span>
      ),
      onClick: () => {
        exportToExcel(dataView, "Export-Numbers", {
          // id: { displayAs: "ID", colWidth: 4 },
          phoneNumber: { displayAs: "Phone Number", colWidth: 14 },
          username: { displayAs: "Username", colWidth: 12 },
          "pool.name": { displayAs: "Pool", colWidth: 15 },
          "sipStatus.name": { displayAs: "Status", colWidth: 12 },
          adStatus: { displayAs: "AD Status", colWidth: 12 },
          type: { displayAs: "Type", colWidth: 10 },
          addedToPoolX: { displayAs: "Added to Pool", colWidth: 15 },
          releaseDateX: { displayAs: "Release Date", colWidth: 15 },
          lastUsedBySystemX: { displayAs: "Last Used by System", colWidth: 18 },
          comment: { displayAs: "Comment", colWidth: 18 },
        });
      },
    },
  ];

  const getSipNumbersDataPromise = async (): Promise<ResponseGuiType> => {
    //LFC: await sipNumbersData to be resolved before dynamically setting xxxFilterOptionValues
    const sipNumbersData = await getSipNumbersData();
    setState((prevState) => ({
      ...prevState,
      sipNumberStatusFilterOptionValues: getSipNumberStatusOptionValues(),
      adStatusFilterOptionValues: getAdStatusOptionValues(),
      typeFilterOptionValues: getTypeOptionValues(),
    }));
    return sipNumbersData;
  };

  return (
    <div className="flex flex-col w-full">
      <div className="flex justify-between items-center mx-8 my-6">
        <h1 className="text-lg font-semibold text-bdo-text-01-charcoal">
          Numbers
        </h1>
        <aside className="flex gap-3">
          {buttons.map((button, index) => {
            return (
              <Button
                key={index}
                onClick={button.onClick}
                className="bdo-red-button"
              >
                {button.title}
              </Button>
            );
          })}
        </aside>
      </div>

      <div className="flex-row w-full">
        <TablePage
          refreshDataToggle={refreshDataToggle}
          setParentDataView={(data) => {
            setState((prevState) => ({ ...prevState, dataView: data }));
          }}
          pageConfiguration={{
            pageName: "Numbers",
            dataApi: getSipNumbersDataPromise,
            columnDefinitions: [
              {
                titleText: "Number",
                dataIndex: "phoneNumberX",
                ignoreChars: " -()",
                render: ({
                  dataRow,
                  cellText = "",
                  searchText = "",
                  ignoreChars = "",
                }) => {
                  return (
                    <div className="relative">
                      <Button
                        type={"link"}
                        onClick={() => {
                          setState((prevState) => ({
                            ...prevState,
                            editNumberRecord: dataRow as TransformedDataType,
                            isEditNumberStatusModalVisible: true,
                          }));
                        }}
                        disabled={!user.isAdmin}
                      >
                        <>
                          <span style={{ userSelect: "text" }}>
                            <TextHighlighter
                              bodyText={cellText}
                              searchText={searchText}
                              ignoreChars={ignoreChars}
                            />
                          </span>
                        </>
                      </Button>
                      <div className="absolute top-1 right-0 w-[32px]">
                        <Tooltip title={dataRow.lastError}>
                          <Badge
                            count={
                              dataRow.lastError && (
                                <span>
                                  <WarningFilled style={{ color: "red" }} />
                                </span>
                              )
                            }
                            className="-mt-2 float-left"
                            color={dataRow.lastError ? "red" : undefined}
                            title={""}
                          ></Badge>
                        </Tooltip>
                        <Tooltip title={dataRow.comment}>
                          <Badge
                            count={
                              dataRow.comment && (
                                <span>
                                  <MdChatBubbleOutline />
                                </span>
                              )
                            }
                            className="-mt-2 right-0 float-right"
                            color={dataRow.comment ? "blue" : undefined}
                            title={""}
                          ></Badge>
                        </Tooltip>
                      </div>
                    </div>
                  );
                },
              },
              {
                titleText: "Username",
                dataIndex: "username",
                render: ({
                  dataRow,
                  cellText = "",
                  searchText = "",
                  ignoreChars = "",
                }) => {
                  let newOrEditButton;
                  const record = dataRow as TransformedDataType;
                  if (user.isAdmin) {
                    if (record.sipStatus.name === "Unassigned") {
                      newOrEditButton = (
                        <MdAddCircleOutline
                          className="text-xl text-[#0062B8] mr-1 cursor-pointer"
                          onClick={() => {
                            setState((prevState) => ({
                              ...prevState,
                              editNumberRecord: record,
                              isAddNumberModalVisible: true,
                            }));
                          }}
                        />
                      );
                    } else if (
                      (record.sipMeetingRoomAssignmentId ||
                        record.sipUserAssignmentId ||
                        record.sipOtherAssignmentId) &&
                      record.sipStatus.name !== "Retired"
                    ) {
                      newOrEditButton = (
                        <Edit
                          className="text-xl text-[#0062B8] mr-1"
                          onClick={() => {
                            setState((prevState) => ({
                              ...prevState,
                              editNumberRecord: record,
                              isEditNumberAssignmentModalVisible: true,
                            }));
                          }}
                        />
                      );
                    }
                  }
                  return (
                    <>
                      {newOrEditButton}
                      <TextHighlighter
                        bodyText={cellText}
                        searchText={searchText}
                        ignoreChars={ignoreChars}
                      />
                    </>
                  );
                },
              },
              {
                titleText: "Pool",
                dataIndex: "pool.name",
              },
              //--
              {
                titleText: "Status",
                dataIndex: "sipStatus.name",
                filterOptionValues: sipNumberStatusFilterOptionValues,
                render: ({
                  dataRow,
                  cellText = "",
                  searchText = "",
                  ignoreChars = "",
                }) => {
                  return <>{cellText}</>;
                },
              },
              {
                titleText: "AD Status",
                dataIndex: "adStatus",
                filterOptionValues: adStatusFilterOptionValues,
                render: ({
                  dataRow,
                  cellText = "",
                  searchText = "",
                  ignoreChars = "",
                }) => {
                  return <>{cellText}</>;
                },
              },
              {
                titleText: "Type",
                dataIndex: "type",
                filterOptionValues: typeFilterOptionValues,
                render: ({
                  dataRow,
                  cellText = "",
                  searchText = "",
                  ignoreChars = "",
                }) => {
                  return <>{cellText}</>;
                },
              },

              //--
              { titleText: "Added to Pool", dataIndex: "addedToPoolX" },
              { titleText: "Release Date", dataIndex: "releaseDateX" },
              {
                titleText: "Last Used by System",
                dataIndex: "lastUsedBySystemX",
              },
            ],
          }}
        />
        <AddNumberAssignmentFormModal
          isVisible={isAddNumberModalVisible}
          handleClose={(refreshData) =>
            setState((prevState) => ({
              ...prevState,
              isAddNumberModalVisible: false,
              ...(refreshData ? { refreshDataToggle: !refreshDataToggle } : {}),
            }))
          }
          sipNumberRecord={editNumberRecord}
        />
        <EditNumberAssignmentFormModal
          isVisible={isEditNumberAssignmentModalVisible}
          handleClose={(refreshData) =>
            setState((prevState) => ({
              ...prevState,
              isEditNumberAssignmentModalVisible: false,
              ...(refreshData ? { refreshDataToggle: !refreshDataToggle } : {}),
            }))
          }
          sipNumberRecord={editNumberRecord}
        />
        <EditNumberStatusFormModal
          isVisible={isEditNumberStatusModalVisible}
          handleClose={(refreshData) =>
            setState((prevState) => ({
              ...prevState,
              isEditNumberStatusModalVisible: false,
              ...(refreshData ? { refreshDataToggle: !refreshDataToggle } : {}),
            }))
          }
          sipNumberRecord={editNumberRecord}
        />
        <EditPoolFormModal
          isVisible={isEditPoolModalVisible}
          handleClose={(refreshData) =>
            setState((prevState) => ({
              ...prevState,
              isEditPoolModalVisible: false,
              ...(refreshData ? { refreshDataToggle: !refreshDataToggle } : {}),
            }))
          }
          editPoolRecordId={editNumberRecord?.pool?.id}
        />
      </div>
    </div>
  );
}

export default Numbers;
